import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAdminSchedules, getScheduleOverview, createSchedule, deleteSchedule,
  removeSession, getAdminTutors, getSchedulingSettings, updateSchedulingSettings,
  getStandbyRequests, getReleasedHours, approveStandbyRequest, rejectStandbyRequest,
  verifyHours,
} from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Calendar, Clock, Users, Sprout, ChevronLeft, ChevronRight, Plus,
  Trash2, Loader2, AlertTriangle, CheckCircle, XCircle, Settings,
  User, LogOut, MoreHorizontal, CalendarDays
} from 'lucide-react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const DAY_FULL = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

function getMonday(dateStr) {
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  return mon.toISOString().split('T')[0];
}

function shiftWeek(weekStart, delta) {
  const d = new Date(weekStart + 'T00:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().split('T')[0];
}

function formatWeek(ws) {
  const start = new Date(ws + 'T00:00:00');
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

const statusIcon = (s) => {
  switch (s) {
    case 'confirmed': return <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" />;
    case 'completed': return <CheckCircle className="w-3.5 h-3.5 text-[#002FA7]" />;
    case 'declined': return <XCircle className="w-3.5 h-3.5 text-[#EF4444]" />;
    case 'released': return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
    default: return <Clock className="w-3.5 h-3.5 text-[#9CA3AF]" />;
  }
};

const statusBadge = (s) => {
  const map = {
    confirmed: 'bg-green-50 text-green-700 border-green-200',
    completed: 'bg-blue-100 text-blue-800 border-blue-300',
    declined: 'bg-red-50 text-red-700 border-red-200',
    released: 'bg-amber-50 text-amber-700 border-amber-200',
    pending: 'bg-gray-50 text-gray-600 border-gray-200',
    partially_confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return <Badge className={map[s] || map.pending}>{s.replace(/_/g, ' ')}</Badge>;
};

export default function AdminScheduling() {
  const { user, logout } = useAuth();
  const [weekStart, setWeekStart] = useState(getMonday());
  const [overview, setOverview] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [settings, setSettings] = useState({ deadline_day: 'wednesday', deadline_time: '17:00' });
  const [loading, setLoading] = useState(true);

  // Create schedule dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState('');
  const [newSessions, setNewSessions] = useState([{ day: 'monday', start_time: '09:00', end_time: '10:00', subject: '', student_name: '' }]);
  const [creating, setCreating] = useState(false);

  // Settings dialog
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [editSettings, setEditSettings] = useState({ deadline_day: 'wednesday', deadline_time: '17:00' });

  // Standby
  const [standbyRequests, setStandbyRequests] = useState({ requests: [], stats: {} });
  const [releasedHours, setReleasedHours] = useState({ released_hours: [], stats: {} });
  const [activeTab, setActiveTab] = useState('schedules');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, schedulesRes, tutorsRes, settingsRes, standbyRes, releasedRes] = await Promise.all([
        getScheduleOverview(weekStart),
        getAdminSchedules(weekStart),
        getAdminTutors(),
        getSchedulingSettings(),
        getStandbyRequests(weekStart),
        getReleasedHours(weekStart),
      ]);
      setOverview(overviewRes.data);
      setSchedules(schedulesRes.data.schedules);
      setTutors(tutorsRes.data.tutors);
      setSettings(settingsRes.data);
      setStandbyRequests(standbyRes.data);
      setReleasedHours(releasedRes.data);
    } catch (err) {
      toast.error('Failed to load scheduling data');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!selectedTutor) { toast.error('Select a tutor'); return; }
    const valid = newSessions.filter(s => s.day && s.start_time && s.end_time);
    if (valid.length === 0) { toast.error('Add at least one session'); return; }
    setCreating(true);
    try {
      await createSchedule({ tutor_id: selectedTutor, week_start: weekStart, sessions: valid });
      toast.success('Schedule created');
      setCreateDialog(false);
      setSelectedTutor('');
      setNewSessions([{ day: 'monday', start_time: '09:00', end_time: '10:00', subject: '', student_name: '' }]);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await deleteSchedule(id);
      toast.success('Schedule deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRemoveSession = async (scheduleId, sessionId) => {
    try {
      await removeSession(scheduleId, sessionId);
      toast.success('Session removed');
      loadData();
    } catch (err) {
      toast.error('Failed to remove session');
    }
  };

  const handleVerifyHours = async (scheduleId) => {
    try {
      const res = await verifyHours(scheduleId, []);
      toast.success(res.data.message || 'Hours verified');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify hours');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await updateSchedulingSettings(editSettings);
      toast.success('Settings saved');
      setSettingsDialog(false);
      loadData();
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const handleApproveStandby = async (requestId) => {
    try {
      await approveStandbyRequest(requestId);
      toast.success('Standby request approved');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectStandby = async (requestId) => {
    try {
      await rejectStandbyRequest(requestId, '');
      toast.success('Standby request rejected');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const addSessionRow = () => setNewSessions(prev => [...prev, { day: 'monday', start_time: '09:00', end_time: '10:00', subject: '', student_name: '' }]);
  const removeSessionRow = (i) => setNewSessions(prev => prev.filter((_, idx) => idx !== i));
  const updateSessionRow = (i, field, value) => setNewSessions(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const compliantTutors = tutors.filter(t => t.fully_compliant);
  const scheduledIds = new Set(schedules.map(s => s.tutor_id));
  const availableTutors = compliantTutors.filter(t => !scheduledIds.has(t.id));

  const stats = overview?.stats || {};

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
                <Sprout className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">Scheduling</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin"><Button variant="outline" size="sm" className="text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Dashboard</Button></Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="text-xs"><User className="w-3 h-3 mr-1" /> {user?.name}</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Week Nav + Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} data-testid="prev-week">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center px-3">
              <p className="text-sm font-semibold text-[#111827]" data-testid="week-display">{formatWeek(weekStart)}</p>
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Week of {weekStart}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} data-testid="next-week">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(getMonday())} className="text-xs text-[#002FA7] ml-1">Today</Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setEditSettings(settings); setSettingsDialog(true); }} data-testid="settings-btn">
              <Settings className="w-3.5 h-3.5 mr-1" /> Settings
            </Button>
            <Button size="sm" onClick={() => setCreateDialog(true)} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="create-schedule-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Assign Hours
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-1 mb-6 p-1 bg-gray-100 rounded-lg w-fit" data-testid="scheduling-tabs">
              {[
                { id: 'schedules', label: 'Schedules', icon: CalendarDays },
                { id: 'standby', label: `Standby Cover${standbyRequests.stats.pending ? ` (${standbyRequests.stats.pending})` : ''}`, icon: Users },
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-[#111827] shadow-sm' : 'text-[#4B5563] hover:text-[#111827]'}`}
                  data-testid={`tab-${tab.id}`}>
                  <tab.icon className="w-3.5 h-3.5" />{tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'schedules' && (
              <>
            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Tutors Scheduled', value: stats.tutors_scheduled || 0, icon: Users },
                { label: 'Total Sessions', value: stats.total_sessions || 0, icon: CalendarDays },
                { label: 'Confirmed', value: stats.confirmed_sessions || 0, icon: CheckCircle, color: 'text-[#10B981]' },
                { label: 'Pending', value: stats.pending_sessions || 0, icon: Clock, color: stats.pending_sessions > 0 ? 'text-amber-500' : 'text-[#9CA3AF]' },
              ].map((s, i) => (
                <Card key={i} className="border border-black/10" data-testid={`sched-stat-${i}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <s.icon className={`w-5 h-5 ${s.color || 'text-[#111827]'}`} />
                    <div><p className="text-xl font-bold text-[#111827]">{s.value}</p><p className="text-xs text-[#4B5563]">{s.label}</p></div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Deadline Info */}
            {overview && (
              <div className={`mb-6 p-3 rounded-lg border text-sm flex items-center gap-2 ${overview.past_deadline ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`} data-testid="deadline-info">
                <Clock className="w-4 h-4 flex-shrink-0" />
                Deadline: {new Date(overview.deadline).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {overview.past_deadline ? ' (PASSED)' : ''}
                {overview.unconfirmed_tutors?.length > 0 && !overview.past_deadline && (
                  <span className="ml-2 font-medium">&middot; {overview.unconfirmed_tutors.length} tutor(s) haven't confirmed</span>
                )}
              </div>
            )}

            {/* Schedule Cards */}
            {schedules.length === 0 ? (
              <Card className="border border-dashed border-gray-300">
                <CardContent className="p-10 text-center">
                  <Calendar className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
                  <p className="text-sm text-[#4B5563]">No schedules for this week.</p>
                  <p className="text-xs text-[#9CA3AF] mt-1">Click "Assign Hours" to create a schedule for a tutor.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {schedules.map((sched) => (
                  <Card key={sched.id} className="border border-black/10" data-testid={`schedule-${sched.id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-base">{sched.tutor_name}</CardTitle>
                          <Badge className="bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20 text-xs">
                            {sched.tutor_type === 'main_tutor' ? 'Main' : 'Standby'}
                          </Badge>
                          {statusBadge(sched.overall_status)}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                          <span>{sched.confirmed_hours}/{sched.total_hours} hrs confirmed</span>
                          {sched.sessions.some(s => s.status === 'confirmed' || s.status === 'assigned_standby') && (
                            <Button
                              size="sm"
                              onClick={() => handleVerifyHours(sched.id)}
                              className="bg-[#002FA7] hover:bg-[#002482] text-white text-xs h-7"
                              data-testid={`verify-hours-${sched.id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" /> Verify Hours
                            </Button>
                          )}
                          {sched.overall_status === 'completed' && (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-xs">All Verified</Badge>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDeleteSchedule(sched.id)} className="text-red-600"><Trash2 className="w-3 h-3 mr-2" /> Delete Schedule</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-20">Day</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>Student</TableHead>
                            <TableHead className="w-16">Hours</TableHead>
                            <TableHead className="w-24">Status</TableHead>
                            <TableHead className="w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sched.sessions.map((sess) => (
                            <TableRow key={sess.session_id}>
                              <TableCell className="text-sm font-medium">{DAY_LABELS[sess.day]}</TableCell>
                              <TableCell className="text-sm">{sess.start_time} – {sess.end_time}</TableCell>
                              <TableCell className="text-sm">{sess.subject || '-'}</TableCell>
                              <TableCell className="text-sm text-[#4B5563]">{sess.student_name || '-'}</TableCell>
                              <TableCell className="text-sm">{sess.hours}h</TableCell>
                              <TableCell><div className="flex items-center gap-1">{statusIcon(sess.status)} <span className="text-xs capitalize">{sess.status}</span></div></TableCell>
                              <TableCell>
                                {sess.status === 'pending' && (
                                  <Button variant="ghost" size="sm" onClick={() => handleRemoveSession(sched.id, sess.session_id)} className="text-red-400 hover:text-red-600">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
              </>
            )}

            {activeTab === 'standby' && (
              <>
                {/* Released Hours Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Total Released', value: releasedHours.stats?.total_released || 0, icon: AlertTriangle, color: 'text-amber-500' },
                    { label: 'Released Hours', value: `${releasedHours.stats?.total_hours || 0}h`, icon: Clock },
                    { label: 'Assigned', value: releasedHours.stats?.assigned || 0, icon: CheckCircle, color: 'text-[#10B981]' },
                    { label: 'Unassigned', value: releasedHours.stats?.unassigned || 0, icon: XCircle, color: (releasedHours.stats?.unassigned || 0) > 0 ? 'text-[#EF4444]' : 'text-[#9CA3AF]' },
                  ].map((s, i) => (
                    <Card key={i} className="border border-black/10" data-testid={`standby-stat-${i}`}>
                      <CardContent className="p-4 flex items-center gap-3">
                        <s.icon className={`w-5 h-5 ${s.color || 'text-[#111827]'}`} />
                        <div><p className="text-xl font-bold text-[#111827]">{s.value}</p><p className="text-xs text-[#4B5563]">{s.label}</p></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Pending Requests */}
                {standbyRequests.requests.filter(r => r.status === 'pending').length > 0 && (
                  <Card className="border border-amber-200 mb-4" data-testid="pending-requests">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" /> Pending Requests ({standbyRequests.stats.pending})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Standby Tutor</TableHead>
                            <TableHead>Original Tutor</TableHead>
                            <TableHead>Day</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>Requested</TableHead>
                            <TableHead className="w-24"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {standbyRequests.requests.filter(r => r.status === 'pending').map(req => (
                            <TableRow key={req.id} data-testid={`request-${req.id}`}>
                              <TableCell className="font-medium text-sm">{req.requesting_tutor_name}</TableCell>
                              <TableCell className="text-sm text-[#4B5563]">{req.original_tutor_name}</TableCell>
                              <TableCell className="text-sm">{DAY_LABELS[req.day]}</TableCell>
                              <TableCell className="text-sm">{req.start_time} – {req.end_time}</TableCell>
                              <TableCell className="text-sm">{req.subject || '-'}</TableCell>
                              <TableCell className="text-sm">{req.hours}h</TableCell>
                              <TableCell className="text-xs text-[#9CA3AF]">{new Date(req.requested_at).toLocaleDateString('en-GB')}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="sm" onClick={() => handleApproveStandby(req.id)} className="bg-[#10B981] hover:bg-[#059669] h-7 text-xs" data-testid={`approve-${req.id}`}>
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleRejectStandby(req.id)} className="border-red-200 text-red-600 h-7 text-xs" data-testid={`reject-${req.id}`}>
                                    <XCircle className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {/* Released Hours */}
                <Card className="border border-black/10" data-testid="released-hours-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Released Hours</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {(releasedHours.released_hours || []).length === 0 ? (
                      <div className="text-center py-8 text-sm text-[#9CA3AF]">No released hours this week</div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Original Tutor</TableHead>
                            <TableHead>Day</TableHead>
                            <TableHead>Time</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>Student</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>Assigned To</TableHead>
                            <TableHead>Requests</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(releasedHours.released_hours || []).map((rh, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-sm">{rh.original_tutor}</TableCell>
                              <TableCell className="text-sm font-medium">{DAY_LABELS[rh.day]}</TableCell>
                              <TableCell className="text-sm">{rh.start_time} – {rh.end_time}</TableCell>
                              <TableCell className="text-sm">{rh.subject || '-'}</TableCell>
                              <TableCell className="text-sm text-[#4B5563]">{rh.student_name || '-'}</TableCell>
                              <TableCell className="text-sm">{rh.hours}h</TableCell>
                              <TableCell>
                                {rh.assigned_to
                                  ? <Badge className="bg-green-50 text-green-700 border-green-200">{rh.assigned_to}</Badge>
                                  : <span className="text-xs text-[#9CA3AF]">Unassigned</span>
                                }
                              </TableCell>
                              <TableCell className="text-sm text-center">{rh.pending_requests || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                {/* History */}
                {standbyRequests.requests.filter(r => r.status !== 'pending').length > 0 && (
                  <Card className="border border-black/10 mt-4" data-testid="request-history">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-[#4B5563]">Request History</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tutor</TableHead>
                            <TableHead>Shift</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {standbyRequests.requests.filter(r => r.status !== 'pending').map(req => (
                            <TableRow key={req.id}>
                              <TableCell className="text-sm">{req.requesting_tutor_name}</TableCell>
                              <TableCell className="text-sm">{DAY_LABELS[req.day]} {req.start_time}–{req.end_time}</TableCell>
                              <TableCell>{statusBadge(req.status)}</TableCell>
                              <TableCell className="text-xs text-[#9CA3AF]">{req.approved_at || req.rejected_at ? new Date(req.approved_at || req.rejected_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Create Schedule Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Assign Hours for Week of {formatWeek(weekStart)}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tutor</Label>
              <Select value={selectedTutor} onValueChange={setSelectedTutor}>
                <SelectTrigger data-testid="select-tutor"><SelectValue placeholder="Select tutor..." /></SelectTrigger>
                <SelectContent>
                  {availableTutors.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.tutor_type === 'main_tutor' ? 'Main' : 'Standby'})</SelectItem>
                  ))}
                  {availableTutors.length === 0 && <SelectItem value="_none" disabled>No available compliant tutors</SelectItem>}
                </SelectContent>
              </Select>
              {availableTutors.length === 0 && <p className="text-xs text-amber-600 mt-1">All compliant tutors are already scheduled, or no tutors are fully compliant.</p>}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Sessions</Label>
                <Button variant="outline" size="sm" onClick={addSessionRow} className="text-xs"><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {newSessions.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 bg-[#F9FAFB] rounded-lg border border-gray-100">
                    <div className="col-span-2">
                      <Label className="text-[10px]">Day</Label>
                      <Select value={s.day} onValueChange={(v) => updateSessionRow(i, 'day', v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{DAY_LABELS[d]}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Start</Label>
                      <Input type="time" className="h-8 text-xs" value={s.start_time} onChange={(e) => updateSessionRow(i, 'start_time', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">End</Label>
                      <Input type="time" className="h-8 text-xs" value={s.end_time} onChange={(e) => updateSessionRow(i, 'end_time', e.target.value)} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-[10px]">Subject</Label>
                      <Input className="h-8 text-xs" placeholder="e.g. Maths" value={s.subject} onChange={(e) => updateSessionRow(i, 'subject', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px]">Student</Label>
                      <Input className="h-8 text-xs" placeholder="Name" value={s.student_name} onChange={(e) => updateSessionRow(i, 'student_name', e.target.value)} />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      {newSessions.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeSessionRow(i)} className="text-red-400 h-8 w-8 p-0"><Trash2 className="w-3 h-3" /></Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="submit-schedule">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calendar className="w-4 h-4 mr-1" />} Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Scheduling Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Confirmation Deadline Day</Label>
              <Select value={editSettings.deadline_day} onValueChange={(v) => setEditSettings(prev => ({ ...prev, deadline_day: v }))}>
                <SelectTrigger data-testid="select-deadline-day"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{DAY_FULL[d]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Deadline Time</Label>
              <Input type="time" value={editSettings.deadline_time} onChange={(e) => setEditSettings(prev => ({ ...prev, deadline_time: e.target.value }))} data-testid="input-deadline-time" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} className="bg-[#002FA7] hover:bg-[#002482]" data-testid="save-settings">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
